import React, { useState, useEffect } from 'react';
import { 
  Users, CheckCircle, Shield, Zap, TrendingUp, BarChart, 
  ArrowRight, Menu, X, ChevronRight, Play, Server, 
  Clock, Globe, Layout, Smartphone, IndianRupee,
  UserCheck, Calendar, DollarSign, Timer, BarChart3, UserCog,
  Bolt, CheckCircle2, Star, Activity, Plus, ShieldCheck
} from 'lucide-react';
import logo from '../assest/logo.png';
import heroImg from '../assest/hero.jpg';
import heroVideo from '../assest/background-hero.mp4';
import '../App.css';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="font-[Poppins] antialiased text-slate-800 bg-white selection:bg-emerald-100 selection:text-emerald-900 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-[100] bg-white h-14 border-b border-slate-100 shadow-sm">
        <div className="max-w-[1240px] mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8">
               <img src={logo} alt="HRMS Software" className="w-full h-full object-contain" />
             </div>
             <span className="text-base font-extrabold tracking-tight text-slate-800">HRMS<span className="text-emerald-500 font-black">Software</span></span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-slate-500 font-semibold text-[13px]">
            <a href="/features" className="hover:text-emerald-500 transition-all hover:scale-105">Features</a>
            <a href="/about" className="hover:text-emerald-600 transition-all hover:scale-105">About Us</a>
            <a href="/solutions" className="hover:text-emerald-600 transition-all hover:scale-105">Solutions</a>
            <a 
              href="/SecureInsights.apk" 
              download="SecureInsights.apk"
              className="px-6 py-2 rounded-[6px] bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 active:scale-95 shadow-[0_10px_30px_rgba(16,185,129,0.2)]"
            >
               Download App
            </a>
          </nav>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-100 shadow-xl z-[150] py-6 px-6 md:hidden">
            <nav className="flex flex-col gap-5 text-slate-600 font-bold text-sm">
              <a href="/" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="/features" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="/about" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <a href="/solutions" onClick={() => setIsMenuOpen(false)}>Solutions</a>
              <a 
                href="/SecureInsights.apk" 
                download="SecureInsights.apk"
                className="w-full py-4 rounded-[6px] bg-emerald-600 text-white shadow-lg text-center"
              >
                Download App
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* --- High-Impact Hero Section --- */}
      <section className="relative min-h-[85vh] lg:min-h-[92vh] flex items-center pt-24 lg:pt-20 pb-10 lg:pb-16 overflow-hidden bg-[#070b14]">
        {/* Bursting Video Background (Coming from background effect) */}
        <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
           <video 
             autoPlay 
             muted 
             loop 
             playsInline 
             className="w-full h-full object-cover hero-video-intro"
           >
              <source src={heroVideo} type="video/mp4" />
           </video>
           {/* Darker overlay to keep text legible */}
           <div className="absolute inset-0 bg-[#070b14]/70 mix-blend-multiply"></div>
        </div>

        <div className="max-w-[1240px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative z-10 w-full">
          <div className="animate-in fade-in slide-in-from-left-12 duration-1000 text-white text-center lg:text-left">
            <div className="inline-flex items-center gap-2.5 px-3 py-1.5 bg-emerald-500/10 rounded-full border border-emerald-500/30 mb-8 font-bold text-[10px] tracking-[0.2em] text-emerald-400 uppercase">
               <Activity size={12} className="text-emerald-500 animate-bounce" />
               Enterprise Intelligence Ecosystem
            </div>
            
            <h1 className="text-3xl md:text-5xl lg:text-[62px] font-extrabold leading-[1.1] lg:leading-[1.05] mb-4 lg:mb-8 tracking-tighter">
              The <span className="relative inline-block">Future<div className="absolute bottom-2 left-0 w-full h-2 bg-emerald-500/20 -z-10"></div></span> of <br className="hidden lg:block"/> 
              Employee <span className="text-emerald-500">Agility.</span>
            </h1>

            <p className="text-sm md:text-lg text-slate-400 mb-6 lg:mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed font-medium opacity-90">
               Automate attendance, streamline payroll, and unlock deep behavioral insights with our next-generation HR catalyst.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
              <a 
                href="/SecureInsights.apk" 
                download="SecureInsights.apk"
                className="group w-full sm:w-auto px-10 py-3.5 lg:py-5 rounded-[10px] bg-emerald-600 text-white font-extrabold text-sm shadow-[0_20px_40px_rgba(16,185,129,0.25)] hover:bg-emerald-700 transition-all flex items-center justify-center gap-4 active:scale-95"
              >
                Download App <Smartphone size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <button className="flex items-center gap-3 text-[13px] font-bold text-slate-300 hover:text-white transition-colors group">
                 <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-emerald-500 transition-all border border-white/20">
                    <Play size={14} className="fill-white translate-x-0.5" />
                 </div>
                 Watch Platform Tour
              </button>
            </div>

            {/* Social Trust */}
            <div className="mt-10 lg:mt-16 flex items-center justify-center lg:justify-start gap-5 border-t border-white/10 pt-6 lg:pt-10">
               <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-[#070b14] bg-slate-800 flex items-center justify-center text-[10px] font-bold text-white">
                       E{i}
                    </div>
                  ))}
               </div>
               <div className="space-y-1">
                  <div className="flex items-center gap-1 text-emerald-500">
                     <Star size={14} fill="currentColor" />
                     <Star size={14} fill="currentColor" />
                     <Star size={14} fill="currentColor" />
                     <Star size={14} fill="currentColor" />
                     <Star size={14} fill="currentColor" />
                  </div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Trusted by 500+ Top Orgs</p>
               </div>
            </div>
          </div>

          <div className="relative animate-in fade-in zoom-in duration-[1500ms] delay-300 flex justify-center">
             {/* Main Graphic */}
             <div className="relative z-10 p-2 bg-gradient-to-tr from-white/10 to-transparent rounded-[24px] border border-white/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.7)] group">
                <div className="rounded-[20px] overflow-hidden">
                   <img 
                     src={heroImg} 
                     alt="Innovation" 
                     className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all duration-[2000ms] scale-105 group-hover:scale-110"
                   />
                </div>
                {/* Image Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b14] via-transparent to-transparent opacity-40"></div>
             </div>

             {/* Floating UI Elements */}
             <div className="absolute -top-10 -right-12 bg-white/10 backdrop-blur-2xl p-6 rounded-[20px] border border-white/20 shadow-2xl animate-float hidden md:block">
                <div className="flex items-center gap-5">
                   <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-500 flex items-center justify-center">
                     <Users size={24} />
                   </div>
                   <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Workforce</p>
                      <p className="text-xl font-extrabold text-white">8.4k+</p>
                   </div>
                </div>
             </div>

             <div className="absolute -bottom-10 -left-12 bg-white/10 backdrop-blur-2xl p-6 rounded-[20px] border border-white/20 shadow-2xl animate-bounce-slow hidden md:block">
                <div className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
                       <Shield size={24} />
                    </div>
                   <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Security</p>
                      <p className="text-xl font-extrabold text-white">100%</p>
                   </div>
                </div>
             </div>

             {/* Background glow behind image */}
             <div className="absolute -inset-10 bg-emerald-500/20 blur-[80px] rounded-full -z-0 opacity-40 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* --- Features Grid --- */}
      <section id="features" className="py-24 relative overflow-hidden bg-white">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none opacity-60"></div>
        <div className="max-w-[1240px] mx-auto px-6 text-center mb-20 relative z-10">
          <h2 className="text-[12px] font-extrabold text-emerald-500 tracking-[0.2em] uppercase mb-4">Functional Catalyst</h2>
          <h3 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4 tracking-tight">Professional Workplace Suite</h3>
          <p className="text-[14px] text-slate-400 max-w-sm mx-auto font-medium opacity-80 leading-relaxed">Integrated architecture for the modern hyper-efficient workforce.</p>
        </div>

        <div className="max-w-[1240px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
          <FeatureCard 
            icon={UserCheck} title="Smart Attendance" 
            desc="Mark attendance with location tracking and photo capture. Real-time GPS verification." 
            bgColor="bg-[#eff6ff]" iconColor="text-blue-600" iconBg="bg-blue-100"
          />
          <FeatureCard 
            icon={Calendar} title="Leave Management" 
            desc="Apply for leaves, track status, and view history. Instant approval notifications." 
            bgColor="bg-[#fff1f2]" iconColor="text-red-500" iconBg="bg-red-100"
          />
          <FeatureCard 
            icon={DollarSign} title="Payroll Insights" 
            desc="View monthly salary slips, download payslips, and track precise earnings." 
            bgColor="bg-[#f0fdf4]" iconColor="text-emerald-600" iconBg="bg-emerald-100"
          />
          <FeatureCard 
            icon={Timer} title="Time Tracking" 
            desc="Automatic time calculation with daily earnings tracking. Real-time work hours." 
            bgColor="bg-[#fffbeb]" iconColor="text-orange-600" iconBg="bg-orange-100"
          />
          <FeatureCard 
            icon={BarChart3} title="Performance Insights" 
            desc="Track attendance patterns, leave history, and deep work statistics accurately." 
            bgColor="bg-[#ecfeff]" iconColor="text-cyan-600" iconBg="bg-cyan-100"
          />
          <FeatureCard 
            icon={UserCog} title="Profile Management" 
            desc="Update personal details, upload profile photo, and manage credentials securely." 
            bgColor="bg-[#f5f3ff]" iconColor="text-purple-600" iconBg="bg-purple-100"
          />
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="about" className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-[1240px] mx-auto px-6 text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
            Why Choose <span className="text-emerald-500 font-extrabold">HRMS Software</span>?
          </h2>
          <p className="text-[15px] text-slate-500 font-medium">Engineered for absolute reliability and organizational scale.</p>
        </div>

        <div className="max-w-[1240px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
           <WhyCard 
             icon={Shield} 
             title="Enterprise Security" 
             desc="Your data is protected with industry-standard encryption and secure authentication."
             items={['End-to-end encryption', 'Secure login tokens', 'Regular security audits']}
             iconBg="bg-blue-50" iconColor="text-blue-600"
           />
           <WhyCard 
             icon={Bolt} 
             title="Fast Performance" 
             desc="Optimized for speed with caching, indexing, and efficient database queries."
             items={['Sub-second page loads', 'Real-time updates', 'Scalable architecture']}
             iconBg="bg-orange-50" iconColor="text-orange-600"
           />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#0a0f1c] relative overflow-hidden">
         <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-emerald-900/10 to-transparent pointer-events-none"></div>
         <div className="max-w-[1240px] mx-auto bg-gradient-to-r from-emerald-600 to-emerald-700 py-16 px-10 rounded-[24px] shadow-2xl relative z-10 overflow-hidden text-center">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">Ready to Simplify Your Work Life?</h2>
            <p className="text-white/80 text-base mb-10 max-w-md mx-auto leading-relaxed">Join hundreds of employees already using HRMS Software to manage work efficiently.</p>
            <button className="bg-white text-emerald-700 font-extrabold text-[15px] px-12 py-5 rounded-full shadow-2xl hover:scale-105 active:scale-95 transition-all mx-auto">
               Get Started Now
            </button>
         </div>
      </section>

      <footer className="bg-[#070b14] py-16 border-t border-white/5">
         <div className="max-w-[1240px] mx-auto px-6 flex flex-col items-center justify-center text-center gap-10">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-xl bg-emerald-500/10 p-2 border border-emerald-500/20">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain filter brightness-0 invert" />
               </div>
               <span className="text-xl font-black text-white tracking-tight uppercase">HRMS Software</span>
            </div>
            
            <p className="text-[10px] md:text-[11px] font-bold text-slate-500 uppercase tracking-[0.4em] max-w-[280px] md:max-w-none leading-relaxed">
               © 2026 HRMS Software — ALL RIGHTS RESERVED
            </p>
            
            <div className="flex items-center gap-8 md:gap-12 text-[11px] font-black text-slate-400">
               <a href="/" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Home</a>
               <a href="/about" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">About Us</a>
               <a href="/features" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Software</a>
               <a href="/solutions" className="hover:text-emerald-500 transition-all uppercase tracking-widest hover:scale-110">Solutions</a>
            </div>
         </div>
      </footer>
    </div>
  );
};

// --- Card Components ---
const FeatureCard = ({ icon: Icon, title, desc, bgColor, iconColor, iconBg }) => (
  <div className={`${bgColor} p-10 rounded-[20px] shadow-[0_10px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_25px_60px_rgba(0,0,0,0.08)] transition-all duration-700 group flex flex-col items-start gap-6 border border-white/40`}>
    <div className={`w-14 h-14 rounded-2xl ${iconBg} ${iconColor} flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform`}>
      <Icon size={26} strokeWidth={2.5} />
    </div>
    <div className="space-y-4">
      <h4 className="text-[19px] font-extrabold text-slate-900 tracking-tight">{title}</h4>
      <p className="text-[14px] text-slate-500 leading-relaxed font-semibold opacity-90">{desc}</p>
    </div>
  </div>
);

const WhyCard = ({ icon: Icon, title, desc, items, iconBg, iconColor }) => (
  <div className="bg-white p-8 md:p-12 rounded-[24px] border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.05)] transition-all duration-700">
    <div className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-6 md:gap-8 mb-8">
       <div className={`w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-[20px] ${iconBg} ${iconColor} flex items-center justify-center shadow-sm`}>
         <Icon size={32} strokeWidth={2.5} />
       </div>
       <div className="space-y-2">
         <h4 className="text-xl font-extrabold text-slate-900">{title}</h4>
         <p className="text-[15px] text-slate-500 leading-relaxed font-medium opacity-80">{desc}</p>
       </div>
    </div>
    <div className="space-y-4 pl-0 md:pl-[96px] flex flex-col items-center md:items-start text-left">
       {items.map((item, idx) => (
         <div key={idx} className="flex items-center gap-4 text-[14px] font-bold text-slate-600">
            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
               <CheckCircle2 size={12} strokeWidth={4} />
            </div>
            {item}
         </div>
       ))}
    </div>
  </div>
);

export default HomePage;
